import { useEffect, useState } from "react";
import PrimaryButton from "./PrimaryButton";
import { submitIntake } from "../services/teaserApi";
import { track } from "../utils/analytics";

// Asked straight after payment, when the reader is most ready to say what
// they want. The report answers the question through the chart: themes and
// the periods linked with them, never a dated promise or advice to stay or go.

const TOPICS = [
  ["relationships", "Relationships"],
  ["family", "Family"],
  ["career", "Career and purpose"],
  ["money", "Money"],
  ["home", "Home and moving"],
  ["growth", "Personal growth"],
];

const SITUATIONS = [
  ["single", "Single"],
  ["relationship", "In a relationship"],
  ["married", "Married"],
  ["separated", "Separated or divorced"],
  ["prefer_not", "Prefer not to say"],
];

const EXAMPLES = [
  "I want to understand my patterns in relationships.",
  "Is this a good season to change careers?",
  "What should I focus on in the next year?",
];

const PAID_ORDER_KEY = "ayupath-paid-request";

/** Set just before checkout, so the thank-you page knows which order it is. */
export function rememberCheckout(requestId) {
  try {
    sessionStorage.setItem(PAID_ORDER_KEY, requestId);
  } catch {
    // Private browsing: the confirmation email carries the link instead.
  }
}

const QUESTION_KEY = "ayupath-own-question:";

/** The question a reader typed in the offer before paying, kept for this tab only. */
export function rememberQuestion(requestId, text) {
  try {
    sessionStorage.setItem(QUESTION_KEY + requestId, text);
  } catch {
    // Private browsing: they can type it again after ordering.
  }
}

export function rememberedQuestion(requestId) {
  try {
    return sessionStorage.getItem(QUESTION_KEY + requestId) || "";
  } catch {
    return "";
  }
}

export function checkoutRequestId() {
  try {
    return sessionStorage.getItem(PAID_ORDER_KEY) || "";
  } catch {
    return "";
  }
}

export default function IntakeForm({ requestId }) {
  const [form, setForm] = useState(() => ({
    question: rememberedQuestion(requestId), topics: [], situation: "", context: "",
  }));
  const [status, setStatus] = useState("idle"); // idle | sending | done | failed | unpaid

  useEffect(() => {
    track("intake_view", { requestId });
  }, [requestId]);

  const set = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  function toggleTopic(value) {
    setForm((current) => {
      const has = current.topics.includes(value);
      if (!has && current.topics.length >= 2) return current;
      return { ...current, topics: has ? current.topics.filter((t) => t !== value) : [...current.topics, value] };
    });
  }

  async function submit(event) {
    event.preventDefault();
    setStatus("sending");
    try {
      await submitIntake(requestId, { ...form, question: form.question.trim(), context: form.context.trim() });
      track("intake_submitted", { requestId });
      setStatus("done");
    } catch (err) {
      setStatus(err.status === 409 ? "unpaid" : "failed");
    }
  }

  if (status === "done") {
    return (
      <div className="rounded-2xl border border-orange-200 bg-orange-50 p-5 text-center space-y-2" role="status">
        <h3 className="font-semibold text-gray-900">Thank you, we have your question</h3>
        <p className="text-sm text-gray-700">Your report will answer it through your chart.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border border-orange-200 bg-white p-5 space-y-5">
      <div className="space-y-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-orange-600">One minute, optional</p>
        <h3 className="text-lg font-semibold text-gray-900">What would you most like your report to help you with?</h3>
      </div>

      <div className="space-y-2">
        <label htmlFor="intake-question" className="sr-only">Your question</label>
        <textarea id="intake-question" rows={3} maxLength={1000} value={form.question}
          onChange={(e) => set("question", e.target.value)}
          placeholder={`For example: ${EXAMPLES[0]}`}
          className="w-full rounded-xl border border-gray-300 p-3 text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-200" />
        <div className="flex flex-wrap gap-1.5">
          {EXAMPLES.map((example) => (
            <button key={example} type="button" onClick={() => set("question", example)}
              className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs text-gray-700 hover:border-orange-300">
              {example}
            </button>
          ))}
        </div>
      </div>

      <fieldset className="space-y-2">
        <legend className="text-sm font-semibold text-gray-900">
          Areas to focus on <span className="font-normal text-gray-500">(up to 2)</span>
        </legend>
        <div className="flex flex-wrap gap-2">
          {TOPICS.map(([value, label]) => (
            <Pill key={value} selected={form.topics.includes(value)} onClick={() => toggleTopic(value)}>{label}</Pill>
          ))}
        </div>
      </fieldset>

      <fieldset className="space-y-2">
        <legend className="text-sm font-semibold text-gray-900">
          Where are you now? <span className="font-normal text-gray-500">(optional)</span>
        </legend>
        <p className="text-xs text-gray-500">So your report never assumes your situation.</p>
        <div className="flex flex-wrap gap-2">
          {SITUATIONS.map(([value, label]) => (
            <Pill key={value} selected={form.situation === value}
              onClick={() => set("situation", form.situation === value ? "" : value)}>{label}</Pill>
          ))}
        </div>
      </fieldset>

      <div className="space-y-2">
        <label htmlFor="intake-context" className="block text-sm font-semibold text-gray-900">
          Anything happening right now we should keep in mind? <span className="font-normal text-gray-500">(optional)</span>
        </label>
        <textarea id="intake-context" rows={2} maxLength={1000} value={form.context}
          onChange={(e) => set("context", e.target.value)}
          className="w-full rounded-xl border border-gray-300 p-3 text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-200" />
      </div>

      {status === "failed" ? (
        <p className="text-sm text-red-700" role="alert">We couldn't save that just now. Please try again.</p>
      ) : null}
      {status === "unpaid" ? (
        <p className="text-sm text-red-700" role="alert">
          We can't see a payment for this order yet. If you have just paid, wait a minute and try again.
        </p>
      ) : null}
      <PrimaryButton type="submit" disabled={!form.question.trim() || status === "sending"}>
        {status === "sending" ? "Sending…" : "Send my question"}
      </PrimaryButton>
      <p className="text-xs text-gray-500">
        Your report answers through what your chart shows, as a lens for reflection. It does not
        predict events or tell you what to decide.
      </p>
    </form>
  );
}

function Pill({ selected, onClick, children }) {
  return (
    <button type="button" aria-pressed={selected} onClick={onClick}
      className={`rounded-full border px-3 py-2 text-xs font-medium transition ${
        selected ? "border-orange-500 bg-orange-500 text-white" : "border-gray-200 bg-white text-gray-700 hover:border-orange-300"
      }`}>
      {children}
    </button>
  );
}
